import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter } from '@rtkwlf/pro-solid-svg-icons/faFilter';
import { useTranslation } from 'react-i18next';

import { useFeatureFlag } from '@platform/core/feature-flags';

import type { StackProps } from '@packages/fenrir/components';
import { Button, Stack, Typography } from '@packages/fenrir/components';
import { useDocumentTitle } from '@packages/fenrir/hooks/use-document-title';

declare module '@platform/core/feature-flags' {
  interface FeatureFlags {
    UNIFIED_PORTAL_IA?: boolean;
  }
}

export interface PageHeaderV2Props extends StackProps {
  title: string;
  documentTitle?: string;
  caption?: string;
  rightComponent?: React.ReactNode;
  showFilter?: boolean;
  onShowFilterChange?: (newFilter: boolean) => void;
}

export const PageHeaderV2 = ({
  title,
  documentTitle,
  caption,
  rightComponent,
  showFilter,
  onShowFilterChange,
  ...rest
}: PageHeaderV2Props) => {
  const { t } = useTranslation('ui');

  useDocumentTitle(documentTitle || title);
  const [unifiedPortalIaFF] = useFeatureFlag('UNIFIED_PORTAL_IA');

  return (
    <Stack width="100%" gap={1} marginBottom={unifiedPortalIaFF ? 3 : 0}>
      <Stack
        width="100%"
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        {...rest}
        flexWrap="wrap"
        gap={2}
      >
        <Typography variant={unifiedPortalIaFF ? 'h4' : 'h3'} component="h1">
          {title}
        </Typography>

        <Stack direction="row" gap={1}>
          {onShowFilterChange && (
            <Button
              startIcon={<FontAwesomeIcon icon={faFilter} />}
              onClick={() => onShowFilterChange(!showFilter)}
            >
              {showFilter ? t('hideFilters') : t('showFilters')}
            </Button>
          )}
          {rightComponent}
        </Stack>
      </Stack>

      {Boolean(caption) && <Typography variant="body2">{caption}</Typography>}
    </Stack>
  );
};
